import React from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import TextField from '@material-ui/core/TextField';
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import classnames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import { withTranslation } from '../../../src/i18n';
import { removeWithTranslationProps } from '../../../utils/common';
import { borderRadius, colors, fontSize } from '../../../constants/styleConstants';

const useMainSearchTextFieldStyles = makeStyles({
  inputField: {
    background: 'transparent',
    borderRadius: '4px',
    borderBottom: 'none',
    height: '36px',
    width: '100% !important',
    '&:hover .MuiInputBase-formControl': {
      borderRadius: '4px',
    },
    '& > label': {
      fontSize: '19px',
    },
    '& > .Mui-focused': {
      color: `${colors.blue3} !important`,
    },
    '& > .MuiInputBase-formControl': {
      border: `1px solid ${colors.black1}`,
      borderRadius: '4px',
      marginTop: '0px',
      '&:hover': {
        borderColor: colors.blue3,
        borderRadius: '4px',
      },
    },
    '&:focus-within label': {
      top: '-8px',
      fontSize: '14px',
      left: '13px',
      padding: '0px 10px',
      color: colors.blue3,
      background: isArtistStubPage => (isArtistStubPage ? colors.background.grayLight : colors.white),
      zIndex: 200,
    },
    '&:focus-within .MuiInputBase-formControl': {
      border: `2px solid ${colors.blue3}`,
      borderRadius: '4px',
      height: '36px',
    },
  },
  input: {
    padding: '10px 15px',
    color: colors.black,
    height: '15px',
  },
  cssLabel: {
    position: 'absolute',
    pointerEvents: 'none',
    left: '12px',
    top: '10px',
    transition: '0.2s ease all',
  },
  cssOutlinedInput: {
    '&$cssFocused $notchedOutline': {
      borderColor: `${colors.blue3} !important`,
    },
    '&:hover:not($disabled):not($focused):not($error) $notchedOutline': {
      borderColor: `${colors.blue3} !important`,
    },
  },
  cssFocused: {},
  notchedOutline: {
    borderWidth: '1px',
    color: `${colors.blue3} !important`,
  },
  underline: {
    '&::after': {
      borderBottom: `1px solid transparent !important`,
      borderBottomRightRadius: '4px',
      borderBottomLeftRadius: '4px',
    },
    '&::before': {
      borderBottom: `1px solid transparent !important`,
      borderBottomRightRadius: '4px',
      borderBottomLeftRadius: '4px',
    },
  },
});

const useTextFieldStyles = makeStyles({
  field: {
    marginTop: 0,
    marginBottom: 0,
    width: '100%',
  },
  disabledField: {
    backgroundColor: `${colors.background.grayLight} !important`,
    color: `${colors.textGray} !important`,
  },
  inputLabel: {
    color: `${colors.title} !important`,
    letterSpacing: 0.6,
    textAlign: 'left',
    '&.Mui-error': {
      color: `${colors.red} !important`,
    },
  },
  input: {
    padding: '8px 10px !important',
    fontSize: fontSize.textFieldLabel,
    height: 29,
    backgroundColor: colors.white,
    '&:focus': {
      backgroundColor: colors.background.gray,
    },
  },
  inputBox: {
    paddingRight: 10,
    margin: 0,
    border: `1px solid ${colors.fieldsBorderGray} !important`,
    borderRadius: borderRadius.fields,
    backgroundColor: colors.white,
    '&>input[type=number]': {
      '-moz-appearance': 'textfield',
      '&::-webkit-outer-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
      '&::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
    },
  },
  focusedField: {
    backgroundColor: colors.background.gray,
  },
  underline: {
    '&::after': {
      borderBottom: '0px solid transparent !important',
    },
    '&::before': {
      borderBottom: '0px solid transparent !important',
    },
  },
  fieldLabelFocused: {},
  asterisk: {
    color: colors.primary,
    lineHeight: `${fontSize.textFieldLabel}px`,
  },
  error: {
    border: `1px solid ${colors.red} !important`,
    color: colors.red,
    '&::after': {
      borderBottom: '0px solid transparent !important',
    },
  },
  withMargin: {
    marginTop: `calc(${fontSize.textFieldLabel}px + 3px) !important`,
  },
  shrink: {
    lineHeight: 'calc(1em + 2px) !important',
    fontSize: `${fontSize.smallTitle}px !important`,
    transform: 'translate(0, 1.5px) scale(1) !important',
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden !important',
    textOverflow: 'ellipsis',
  },
});

function FormikTextField({
  t,
  field,
  form: { errors, touched },
  id,
  inputLabelProps,
  inputProps,
  label,
  required,
  inputClasses,
  className,
  nativeInputProps,
  isMainArtistSearchFilter,
  ...rest
}) {
  const errorText = get(errors, field.name, '')
    .split(' ')
    .map(text => t(text))
    .join(' ');
  const router = useRouter();
  const isArtistStubPage = !isUndefined(get(router, 'query.id'));
  const hasError = get(touched, field.name) && Boolean(get(errors, field.name));
  const classes = useTextFieldStyles();
  const additionalClasses = useMainSearchTextFieldStyles(isArtistStubPage);

  const focusSearchArtistInputField = input => {
    const hasInputValue = get(field, 'value');
    const hasQuery = get(field, 'name');

    if (!isEmpty(hasInputValue) && hasQuery && input) {
      input.focus();
    }
  };

  const searchArtistRef = !isArtistStubPage & isMainArtistSearchFilter && focusSearchArtistInputField;

  return (
    <TextField
      id={id}
      margin="normal"
      label={label}
      className={classnames(classes.field, className, isMainArtistSearchFilter && additionalClasses.inputField)}
      required={required}
      error={hasError}
      helperText={errorText}
      {...field}
      {...removeWithTranslationProps(rest)}
      InputLabelProps={{
        shrink: true,
        classes: {
          root: isMainArtistSearchFilter ? additionalClasses.cssLabel : classes.inputLabel,
          focused: isMainArtistSearchFilter ? additionalClasses.focused : classes.fieldLabelFocused,
          asterisk: classes.asterisk,
          shrink: !isMainArtistSearchFilter && classes.shrink,
        },
        ...inputLabelProps,
      }}
      inputRef={searchArtistRef}
      InputProps={{
        classes: {
          root: classnames(
            !isMainArtistSearchFilter && classes.inputBox,
            isMainArtistSearchFilter ? additionalClasses.cssOutlinedInput : { [classes.withMargin]: Boolean(label) },
          ),
          input: isMainArtistSearchFilter ? additionalClasses.input : classes.input,
          underline: isMainArtistSearchFilter ? additionalClasses.underline : classes.underline,
          disabled: !isMainArtistSearchFilter && classes.disabledField,
          error: !isMainArtistSearchFilter && classes.error,
          focused: isMainArtistSearchFilter ? additionalClasses.cssFocused : classes.focusedField,
          notchedOutline: isMainArtistSearchFilter && additionalClasses.notchedOutline,
          ...inputClasses,
        },
        autoComplete: 'off',
        ...inputProps,
      }}
      // eslint-disable-next-line react/jsx-no-duplicate-props
      inputProps={nativeInputProps}
    />
  );
}

FormikTextField.getInitialProps = async () => ({
  namespacesRequired: ['translation'],
});

FormikTextField.propTypes = {
  t: PropTypes.func.isRequired,
  field: PropTypes.object,
  form: PropTypes.shape({
    errors: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    touched: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  }),
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  inputLabelProps: PropTypes.object,
  inputProps: PropTypes.object,
  label: PropTypes.string,
  required: PropTypes.bool,
  inputClasses: PropTypes.object,
  className: PropTypes.string,
  isMainArtistSearchFilter: PropTypes.bool,
};

FormikTextField.defaultProps = {
  form: {
    errors: {},
    touched: {},
  },
  field: {
    name: '',
  },
};

export default withTranslation('translation')(FormikTextField);
